import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { matchKeywordCount } from './questionScoring';

// WitnessSignOff: simulates a controlled-drug register entry / witnessed
// administration check. Student completes a structured form with the
// required CD-register fields. Engine grade = correct only if every field
// with expected keywords meets a minimum threshold (>=50% of keywords
// present).
//
// Schema:
// {
//   questionType: 'WitnessSignOff',
//   questionTitle, questionText,
//   scenarioContext: 'Schedule 2 CD - oral morphine 10mg/5mL ...',
//   expectedFields: {
//     drug: { label: 'Drug name', keywords: ['morphine','oramorph'] },
//     form: { label: 'Form/strength', keywords: ['oral','solution','10','5'] },
//     dose: { label: 'Dose given', keywords: ['10','mg'] },
//     quantityWords: { label: 'Quantity (in words)', keywords: ['ten','milligrams'] },
//     stockBefore: { label: 'Stock balance before', keywords: ['200'] },
//     stockAfter: { label: 'Stock balance after', keywords: ['190'] },
//     witnessRole: { label: 'Witness role', keywords: ['nurse','pharmacist','registered'] }
//   }
// }

const FIELD_ORDER = [
  'drug',
  'form',
  'dose',
  'quantityWords',
  'stockBefore',
  'stockAfter',
  'witnessRole',
];

const WitnessSignOff = ({ question }) => {
  const fields = useMemo(() => question?.expectedFields || {}, [question]);
  const fieldKeys = useMemo(
    () => FIELD_ORDER.filter((k) => fields[k]),
    [fields],
  );

  const [values, setValues] = useState(() => {
    const init = {};
    fieldKeys.forEach((k) => { init[k] = ''; });
    return init;
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const results = useMemo(() => {
    if (!submitted) return null;
    return fieldKeys.map((key) => {
      const def = fields[key];
      const kws = def?.keywords || [];
      const hits = matchKeywordCount(values[key], kws);
      const target = kws.length;
      const ok = target === 0 || hits >= Math.ceil(target / 2);
      return { key, label: def?.label || key, hits, target, ok };
    });
  }, [submitted, fieldKeys, fields, values]);

  const overall = useMemo(() => {
    if (!results) return null;
    const correct = results.every((r) => r.ok);
    const correctCount = results.filter((r) => r.ok).length;
    return { correct, correctCount, total: results.length };
  }, [results]);

  const canSubmit = !submitted && fieldKeys.some((k) => values[k].trim().length > 0);

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded witness-signoff-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {submitted && overall ? (
          overall.correct
            ? <Badge bg="success">CD register entry complete + correct</Badge>
            : <Badge bg="warning" text="dark">{overall.correctCount} / {overall.total} fields correct</Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-2">{question.questionText}</p>
      {question.scenarioContext ? (
        <div className="alert alert-info py-2 mb-3 small">
          <strong>Scenario:</strong> {question.scenarioContext}
        </div>
      ) : null}

      <Row className="g-2">
        {fieldKeys.map((key) => {
          const def = fields[key];
          const r = results ? results.find((x) => x.key === key) : null;
          return (
            <Col key={key} xs={12} md={key === 'witnessRole' || key === 'stockBefore' || key === 'stockAfter' ? 4 : 6}>
              <Form.Group>
                <Form.Label className="small mb-1">{def?.label || key}</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={values[key]}
                  disabled={submitted}
                  onChange={(e) => update(key, e.target.value)}
                />
                {submitted && r ? (
                  <div className="small mt-1">
                    <Badge bg={r.ok ? 'success' : 'warning'} text={r.ok ? 'light' : 'dark'}>
                      {r.hits} / {r.target} key elements
                    </Badge>
                  </div>
                ) : null}
              </Form.Group>
            </Col>
          );
        })}
      </Row>

      {!submitted ? (
        <Button
          type="button"
          variant="primary"
          className="mt-3"
          disabled={!canSubmit}
          onClick={() => setSubmitted(true)}
        >
          Submit CD register entry
        </Button>
      ) : (
        question.modelAnswer ? (
          <div className="mt-3 p-2 bg-white rounded border small">
            <strong>Model entry:</strong> {question.modelAnswer}
          </div>
        ) : null
      )}
    </Container>
  );
};

export default WitnessSignOff;
